import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { useSubscription } from '@/hooks/useSubscription';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Crown, Star, Plane, Check, ArrowUp, Calendar, TrendingUp, Luggage, Heart, Users, Zap, Globe, Clock, Gift } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckoutConfirmationModal } from '@/components/checkout/CheckoutConfirmationModal';
const subscriptionPlans = {
  economy: {
    name: 'Economy Pack',
    price: 'Free',
    originalPrice: null,
    description: 'Perfect for occasional travelers',
    icon: Plane,
    gradient: 'from-slate-500 to-slate-600',
    popular: false,
    features: ['Basic Packing Lists - 3 Daily Uses', 'Visa Requirements Check - 3 Daily Uses', 'Jet Lag Minimizer - 3 Daily Uses']
  },
  premium_economy: {
    name: 'Premium Economy Pack',
    price: '€6.99',
    originalPrice: null,
    description: 'Enhanced travel planning tools',
    icon: Star,
    gradient: 'from-[#1a9a9a] to-[#6dd4d4]',
    popular: true,
    features: ['Unlimited Searches To All Features', 'AI-Powered Personalized Lists', 'Local Destination Tips', 'Health & Wellness Preparation', 'Jet Lag Minimizer', 'Smart Currency Converter', 'Group Expense Sharing', 'Early Access To New Features']
  },
  first_class: {
    name: 'First Class Pack',
    price: '€11.99',
    originalPrice: null,
    description: 'Premium experience for frequent travelers',
    icon: Crown,
    gradient: 'from-yellow-500 to-yellow-600',
    popular: false,
    features: ['Everything In Premium Economy', 'Offline Mode - Access Lists Without Internet', 'Multi-City Packing Lists', 'Custom Travel Templates - Save & Reuse Lists', 'Advanced Travel Analytics - Spending & Travel Insights', 'Group Packing Progress Sharing']
  }
};
const boostPasses = {
  premium_economy: {
    name: 'Premium Economy Pack Access',
    icon: Star,
    gradient: 'from-[#1a9a9a] to-[#6dd4d4]',
    passes: [{
      duration: '3-Day',
      price: '€2.99',
      badge: 'Quick Trip'
    }, {
      duration: '7-Day',
      price: '€5.99',
      badge: 'Popular Choice'
    }, {
      duration: '14-Day',
      price: '€9.99',
      badge: 'Best Value'
    }]
  },
  first_class: {
    name: 'First Class Pack Access',
    icon: Crown,
    gradient: 'from-yellow-500 to-yellow-600',
    passes: [{
      duration: '3-Day',
      price: '€4.99',
      badge: 'Premium Choice'
    }, {
      duration: '7-Day',
      price: '€10.99',
      badge: 'Most Popular'
    }, {
      duration: '14-Day',
      price: '€16.99',
      badge: 'Maximum Value'
    }]
  }
};
export function SubscriptionSection() {
  const {
    subscription,
    loading,
    refreshSubscription
  } = useSubscription();
  const {
    toast
  } = useToast();
  const [upgradeLoading, setUpgradeLoading] = useState<string | null>(null);
  const [giftModalOpen, setGiftModalOpen] = useState(false);
  const [giftTier, setGiftTier] = useState<'premium_economy' | 'first_class' | null>(null);
  const [selectedGiftOption, setSelectedGiftOption] = useState('');
  const [giftForm, setGiftForm] = useState({
    recipientName: '',
    recipientEmail: '',
    senderName: '',
    message: ''
  });
  const [giftLoading, setGiftLoading] = useState(false);
  const [boostLoading, setBoostLoading] = useState<string | null>(null);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<{
    type: 'subscription' | 'gift' | 'boost';
    tier: string;
    duration?: string;
    price: number;
    giftDetails?: {
      recipientName: string;
      recipientEmail: string;
      message: string;
    };
  } | null>(null);
  const currentPlan = subscriptionPlans[subscription.subscription_tier];
  const isSubscribed = subscription.subscribed;
  const giftOptions = {
    premium_economy: [{
      value: '1month-6.99',
      label: '1 Month - €6.99',
      duration: '1 Month',
      price: '€6.99'
    }, {
      value: '3months-19.50',
      label: '3 Months - €19.50 (Save 7%)',
      duration: '3 Months',
      price: '€19.50'
    }, {
      value: '6months-36.49',
      label: '6 Months - €36.49 (Save 13%)',
      duration: '6 Months',
      price: '€36.49'
    }, {
      value: 'annual-67.10',
      label: 'Annual - €67.10 (Save 20%)',
      duration: 'Annual',
      price: '€67.10'
    }],
    first_class: [{
      value: '1month-11.99',
      label: '1 Month - €11.99',
      duration: '1 Month',
      price: '€11.99'
    }, {
      value: '3months-33.45',
      label: '3 Months - €33.45 (Save 7%)',
      duration: '3 Months',
      price: '€33.45'
    }, {
      value: '6months-62.59',
      label: '6 Months - €62.59 (Save 13%)',
      duration: '6 Months',
      price: '€62.59'
    }, {
      value: 'annual-115.10',
      label: 'Annual - €115.10 (Save 20%)',
      duration: 'Annual',
      price: '€115.10'
    }]
  };
  const handleUpgrade = async (targetTier: string) => {
    const plan = subscriptionPlans[targetTier as keyof typeof subscriptionPlans];
    if (!plan) return;

    const priceValue = plan.price === 'Free' ? 0 : parseFloat(plan.price.replace('€', ''));

    setPendingPurchase({
      type: 'subscription',
      tier: targetTier,
      duration: 'Monthly',
      price: priceValue
    });
    setCheckoutModalOpen(true);
  };

  const handleConfirmCheckout = async (couponCode?: string) => {
    if (!pendingPurchase) return;

    if (pendingPurchase.type === 'subscription') {
      setUpgradeLoading(pendingPurchase.tier);
      try {
        const { data, error } = await supabase.functions.invoke('create-checkout', {
          body: {
            tier: pendingPurchase.tier,
            return_url: window.location.href,
            coupon_code: couponCode
          }
        });
        if (error) throw error;
        if (data?.url) {
          window.location.href = data.url;
        }
      } catch (error) {
        console.error('Error creating checkout:', error);
        toast({
          title: "Error",
          description: "Failed to start upgrade process. Please try again.",
          variant: "destructive"
        });
        throw error;
      } finally {
        setUpgradeLoading(null);
      }
    } else if (pendingPurchase.type === 'gift' && pendingPurchase.giftDetails) {
      setGiftLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke('create-gift-checkout', {
          body: {
            recipientName: pendingPurchase.giftDetails.recipientName,
            recipientEmail: pendingPurchase.giftDetails.recipientEmail,
            senderName: pendingPurchase.giftDetails.recipientName,
            message: pendingPurchase.giftDetails.message,
            tier: pendingPurchase.tier,
            duration: pendingPurchase.duration
          }
        });
        if (error) throw error;
        if (data?.url) {
          window.location.href = data.url;
          setCheckoutModalOpen(false);
          setGiftModalOpen(false);
        }
      } catch (error) {
        console.error('Error creating gift checkout:', error);
        throw error;
      } finally {
        setGiftLoading(false);
      }
    } else if (pendingPurchase.type === 'boost') {
      const boostKey = `${pendingPurchase.tier}-${pendingPurchase.duration}`;
      setBoostLoading(boostKey);
      try {
        const { data, error } = await supabase.functions.invoke('create-boost-checkout', {
          body: {
            tier: pendingPurchase.tier,
            duration: pendingPurchase.duration
          }
        });
        if (error) throw error;
        if (data?.url) {
          window.location.href = data.url;
          setCheckoutModalOpen(false);
        }
      } catch (error) {
        console.error('Error creating boost checkout:', error);
        throw error;
      } finally {
        setBoostLoading(null);
      }
    }
  };
  const handleManageSubscription = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: { return_url: window.location.href }
      });
      // Extract error message from function response (Supabase wraps non-2xx as FunctionsHttpError)
      const errMsg: string | undefined =
        (data && (data as any).error) ||
        (error as any)?.context?.error ||
        (error as any)?.message;
      if (errMsg && /no stripe customer/i.test(errMsg)) {
        toast({
          title: "No billing account yet",
          description:
            "Your access was granted manually, so there's no Stripe billing to manage. Contact support if you need changes.",
        });
        return;
      }
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("No portal URL returned");
    } catch (err) {
      console.error('Error opening customer portal:', err);
      toast({
        title: "Error",
        description: "Failed to open subscription management. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleGiftClick = (tier: 'premium_economy' | 'first_class') => {
    setGiftTier(tier);
    setSelectedGiftOption('');
    setGiftModalOpen(true);
  };
  const handleGiftSubmit = async () => {
    if (!giftForm.recipientName || !giftForm.recipientEmail || !giftForm.senderName) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }
    if (!selectedGiftOption || !giftTier) {
      toast({
        title: "Missing Gift Selection",
        description: "Please select a gift option.",
        variant: "destructive"
      });
      return;
    }
    const selectedOption = giftOptions[giftTier].find(opt => opt.value === selectedGiftOption);
    if (!selectedOption) return;

    setPendingPurchase({
      type: 'gift',
      tier: giftTier,
      duration: selectedOption.duration,
      price: parseFloat(selectedOption.price.replace('€', '')),
      giftDetails: {
        recipientName: giftForm.recipientName,
        recipientEmail: giftForm.recipientEmail,
        message: giftForm.message
      }
    });
    setCheckoutModalOpen(true);
  };
  const handleBoostPass = async (tier: 'premium_economy' | 'first_class', duration: string) => {
    const boostInfo = boostPasses[tier].passes.find(p => p.duration === duration);
    if (!boostInfo) return;

    setPendingPurchase({
      type: 'boost',
      tier,
      duration,
      price: parseFloat(boostInfo.price.replace('€', ''))
    });
    setCheckoutModalOpen(true);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  const getDaysUntilRenewal = () => {
    if (!subscription.subscription_end) return 0;
    const endDate = new Date(subscription.subscription_end);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  const getUsageProgress = () => {
    // Mock usage data - in real app, this would come from analytics
    return {
      trips: {
        used: 3,
        limit: subscription.subscription_tier === 'economy' ? 5 : 'unlimited'
      },
      countries: {
        used: 8,
        limit: subscription.subscription_tier === 'economy' ? 10 : 'unlimited'
      },
      features: {
        used: 4,
        limit: subscription.subscription_tier === 'economy' ? 5 : 'unlimited'
      }
    };
  };
  const usage = getUsageProgress();
  if (loading) {
    return <div className="flex items-center justify-center p-12">
        
      </div>;
  }
  return <div className="space-y-4 p-4">
      {/* Compact Header */}
      <div className="text-center space-y-2 py-2">
        
      </div>

      {/* Current Plan - Compact */}
      <Card className={`relative overflow-hidden border ${currentPlan.gradient ? 'border-primary/50' : 'border-border'}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-gradient-to-r ${currentPlan.gradient}`}>
                <currentPlan.icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  {currentPlan.name}
                  <Badge variant="secondary" className="bg-primary/10 text-primary text-xs px-2 py-0">
                    Current
                  </Badge>
                </CardTitle>
                <CardDescription className="text-sm">{currentPlan.description}</CardDescription>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xl font-bold text-primary">
                {currentPlan.price}
                {currentPlan.price !== 'Free' && <span className="text-sm text-muted-foreground">/month</span>}
              </div>
              {isSubscribed && subscription.subscription_end && <p className="text-xs text-muted-foreground">
                  Until {formatDate(subscription.subscription_end)}
                </p>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                <Check className="h-3 w-3 text-primary" />
                Plan Features
              </h4>
              <div className="grid grid-cols-1 gap-1 max-h-32 overflow-y-auto">
                {currentPlan.features.slice(0, 4).map((feature, index) => <div key={index} className="flex items-start gap-2 text-xs">
                    <Check className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>)}
                {currentPlan.features.length > 4 && <span className="text-xs text-muted-foreground">+{currentPlan.features.length - 4} more features</span>}
              </div>
            </div>
            
            {isSubscribed && <div>
                <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-primary" />
                  Status
                </h4>
                <div className="space-y-1">
                  <div className="flex justify-between items-center text-xs">
                    <span>Status</span>
                    <Badge className="bg-green-100 text-green-800 text-xs px-1 py-0">Active</Badge>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span>Days left</span>
                    <span className="font-medium">{getDaysUntilRenewal()}</span>
                  </div>
                </div>
              </div>}
          </div>

          <div className="flex gap-2 pt-2">
            {isSubscribed && <Button onClick={handleManageSubscription} size="sm" className="text-xs">
                <Crown className="h-3 w-3 mr-1" />
                Manage
              </Button>}
            <Button onClick={refreshSubscription} variant="outline" size="sm" className="text-xs">
              <TrendingUp className="h-3 w-3 mr-1" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Usage Statistics - Compact */}
      {isSubscribed && <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="p-1 rounded bg-gradient-to-r from-cyan-500 to-cyan-500">
                <TrendingUp className="h-4 w-4 text-white" />
              </div>
              Usage This Month
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200/50 dark:border-blue-800/50">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium flex items-center gap-1">
                    <Luggage className="h-3 w-3 text-cyan-600" />
                    Trips
                  </span>
                  <span className="text-xs font-bold text-cyan-600">
                    {usage.trips.used} / {usage.trips.limit === 'unlimited' ? '∞' : usage.trips.limit}
                  </span>
                </div>
                <Progress value={usage.trips.limit === 'unlimited' ? 30 : usage.trips.used / (usage.trips.limit as number) * 100} className="h-1" />
              </div>
              <div className="p-2 rounded-lg bg-[#6dd4d4]/10 dark:bg-[#1a9a9a]/20 border border-[#6dd4d4]/50 dark:border-[#1a9a9a]/50">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium flex items-center gap-1">
                    <Globe className="h-3 w-3 text-[#1a9a9a]" />
                    Countries
                  </span>
                  <span className="text-xs font-bold text-[#1a9a9a]">
                    {usage.countries.used} / {usage.countries.limit === 'unlimited' ? '∞' : usage.countries.limit}
                  </span>
                </div>
                <Progress value={usage.countries.limit === 'unlimited' ? 60 : usage.countries.used / (usage.countries.limit as number) * 100} className="h-1" />
              </div>
              <div className="p-2 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200/50 dark:border-green-800/50">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-medium flex items-center gap-1">
                    <Zap className="h-3 w-3 text-green-600" />
                    Features
                  </span>
                  <span className="text-xs font-bold text-green-600">
                    {usage.features.used} / {usage.features.limit === 'unlimited' ? '∞' : usage.features.limit}
                  </span>
                </div>
                <Progress value={usage.features.limit === 'unlimited' ? 45 : usage.features.used / (usage.features.limit as number) * 100} className="h-1" />
              </div>
            </div>
          </CardContent>
        </Card>}

      {/* Upgrade Options - Compact */}
      {subscription.subscription_tier !== 'first_class' && <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <div className="p-1 rounded bg-gradient-to-r from-[#1a9a9a] to-[#6dd4d4]">
                <ArrowUp className="h-4 w-4 text-white" />
              </div>
              Upgrade Options
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-3">
                Compare all plans and features on our pricing page
              </p>
              <Button onClick={() => window.location.href = '/pricing'} className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-white" size="sm">
                <ArrowUp className="h-3 w-3 mr-1" />
                View All Plans
              </Button>
            </div>
          </CardContent>
        </Card>}

      {/* Quick Upgrade Cards - Compact */}
      {subscription.subscription_tier === 'economy' && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          {/* Premium Economy Quick Card */}
          <Card className="border-primary/20">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded bg-gradient-to-r from-primary to-accent">
                  <Star className="h-4 w-4 text-white" />
                </div>
                <div>
                  <CardTitle className="text-sm">Premium Economy Class Pack</CardTitle>
                  <CardDescription className="text-xs">Most popular</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-primary">€6.99</span>
                <span className="text-xs text-muted-foreground">/month</span>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {subscriptionPlans.premium_economy.features.map((feature, idx) => <div key={idx} className="flex items-center gap-1 text-xs">
                    <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>)}
              </div>
              <Button onClick={() => handleUpgrade('premium_economy')} disabled={upgradeLoading === 'premium_economy'} className="w-full text-xs" size="sm">
                {upgradeLoading === 'premium_economy' ? 'Processing...' : 'Upgrade Now'}
              </Button>
            </CardContent>
          </Card>

          {/* First Class Quick Card */}
          <Card className="border-yellow-500/20">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="p-1 rounded bg-gradient-to-r from-yellow-500 to-yellow-600">
                  <Crown className="h-4 w-4 text-white" />
                </div>
                <div>
                  <CardTitle className="text-sm">First Class Pack</CardTitle>
                  <CardDescription className="text-xs">Ultimate experience</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-yellow-600">€11.99</span>
                <span className="text-xs text-muted-foreground">/month</span>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {subscriptionPlans.first_class.features.map((feature, idx) => <div key={idx} className="flex items-center gap-1 text-xs">
                    <Check className="h-3 w-3 text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>)}
              </div>
              <Button onClick={() => handleUpgrade('first_class')} disabled={upgradeLoading === 'first_class'} className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white text-xs" size="sm">
                {upgradeLoading === 'first_class' ? 'Processing...' : 'Upgrade Now'}
              </Button>
            </CardContent>
          </Card>
        </div>}

      {/* OdyVoy Boost Section - Compact */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <div className="p-1 rounded bg-gradient-to-r from-[#1a9a9a] to-[#6dd4d4]">
              <Clock className="h-4 w-4 text-white" />
            </div>
            OdyVoy Boost - Short-Term Access
          </CardTitle>
          <CardDescription className="text-sm">
            Temporary access to premium features without subscription
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {Object.entries(boostPasses).map(([type, boost]) => <Card key={type} className="border-border/50">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className={`p-1 rounded bg-gradient-to-r ${boost.gradient}`}>
                      <boost.icon className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="text-sm font-bold">{boost.name}</h3>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {boost.passes.map((pass, index) => {
                const boostKey = `${type}-${pass.duration}`;
                const isLoading = boostLoading === boostKey;
                return <div key={index} className="flex items-center justify-between p-2 rounded bg-muted/20">
                        <span className="text-xs font-medium">{pass.duration} Pass</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-primary">{pass.price}</span>
                          <Button size="sm" variant="outline" className="text-xs h-6 px-2" onClick={() => handleBoostPass(type as 'premium_economy' | 'first_class', pass.duration)} disabled={!!boostLoading}>
                            {isLoading ? 'Processing...' : 'Get Pass'}
                          </Button>
                        </div>
                      </div>;
              })}
                </CardContent>
              </Card>)}
          </div>
        </CardContent>
      </Card>

      {/* Benefits Highlight - Compact */}
      <Card>
        <CardHeader className="pb-2 text-center">
          <CardTitle className="text-lg font-bold">Why Upgrade?</CardTitle>
          <CardDescription className="text-sm">
            Key benefits of premium plans
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mx-auto">
                <Luggage className="h-6 w-6 text-cyan-600" />
              </div>
              <h4 className="font-semibold text-sm">Smart Packing</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                AI-powered packing recommendations and custom lists
              </p>
            </div>
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1a9a9a]/20 to-[#6dd4d4]/20 rounded-xl flex items-center justify-center mx-auto">
                <Heart className="h-6 w-6 text-[#1a9a9a]" />
              </div>
              <h4 className="font-semibold text-sm">Health Hub</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Personalized wellness companion with enhanced tracking
              </p>
            </div>
            <div className="text-center space-y-2">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl flex items-center justify-center mx-auto">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <h4 className="font-semibold text-sm">Group Travel</h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Coordinate with companions and share progress
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gift Card Section */}
      <Card className="border-2 border-primary/20">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-gradient-to-r from-[#1a9a9a] to-[#6dd4d4]">
              <Gift className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base">Gift a Subscription</CardTitle>
              <CardDescription className="text-xs">
                Perfect for travelers, business professionals, and adventure seekers
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Share the joy of seamless travel planning with friends, family, or colleagues.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Premium Economy Gift */}
            <Card className="border border-primary/30">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-primary" />
                  <CardTitle className="text-sm">Premium Economy Pack Gift</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">1 Month</span>
                    <span className="font-bold text-primary">€6.99</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">3 Months</span>
                    <div className="flex items-baseline gap-1">
                      <span className="font-bold text-primary">€19.50</span>
                      <Badge className="bg-green-500/10 text-green-600 border-0 text-[10px] py-0 px-1 h-4">Save 7%</Badge>
                    </div>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">6 Months</span>
                    <div className="flex items-baseline gap-1">
                      <span className="font-bold text-primary">€36.49</span>
                      <Badge className="bg-green-500/10 text-green-600 border-0 text-[10px] py-0 px-1 h-4">Save 13%</Badge>
                    </div>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">Annual</span>
                    <div className="flex items-baseline gap-1">
                      <span className="font-bold text-primary">€67.10</span>
                      <Badge className="bg-green-500/10 text-green-600 border-0 text-[10px] py-0 px-1 h-4">Save 20%</Badge>
                    </div>
                  </div>
                </div>
                <Button onClick={() => handleGiftClick('premium_economy')} size="sm" className="w-full text-xs bg-gradient-to-r from-primary to-accent">
                  <Heart className="h-3 w-3 mr-1" />
                  Send Gift
                </Button>
              </CardContent>
            </Card>

            {/* First Class Gift */}
            <Card className="border border-yellow-500/30">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Crown className="h-4 w-4 text-yellow-600" />
                  <CardTitle className="text-sm">First Class Pack Gift</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">1 Month</span>
                    <span className="font-bold text-yellow-600">€11.99</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">3 Months</span>
                    <div className="flex items-baseline gap-1">
                      <span className="font-bold text-yellow-600">€33.45</span>
                      <Badge className="bg-green-500/10 text-green-600 border-0 text-[10px] py-0 px-1 h-4">Save 7%</Badge>
                    </div>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">6 Months</span>
                    <div className="flex items-baseline gap-1">
                      <span className="font-bold text-yellow-600">€62.59</span>
                      <Badge className="bg-green-500/10 text-green-600 border-0 text-[10px] py-0 px-1 h-4">Save 13%</Badge>
                    </div>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-muted-foreground">Annual</span>
                    <div className="flex items-baseline gap-1">
                      <span className="font-bold text-yellow-600">€115.10</span>
                      <Badge className="bg-green-500/10 text-green-600 border-0 text-[10px] py-0 px-1 h-4">Save 20%</Badge>
                    </div>
                  </div>
                </div>
                <Button onClick={() => handleGiftClick('first_class')} size="sm" className="w-full text-xs bg-gradient-to-r from-yellow-500 to-yellow-600">
                  <Heart className="h-3 w-3 mr-1" />
                  Send Gift
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="bg-primary/5 rounded-lg p-3 border border-primary/20">
            <p className="text-xs text-muted-foreground">
              <Check className="h-3 w-3 inline text-primary mr-1" />
              Recipients receive instant email with activation instructions
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              <Check className="h-3 w-3 inline text-primary mr-1" />
              Add a personal message to make it extra special
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Gift Modal */}
      <Dialog open={giftModalOpen} onOpenChange={setGiftModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Gift className="h-6 w-6 text-primary" />
              Send a Gift Subscription
            </DialogTitle>
            <DialogDescription>
              {giftTier && <div className="mt-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <p className="font-semibold text-foreground">
                    {giftTier === 'premium_economy' ? 'Premium Economy Pack' : 'First Class Pack'}
                  </p>
                </div>}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="giftOption">Select Gift Option *</Label>
              <Select value={selectedGiftOption} onValueChange={setSelectedGiftOption}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose duration and price" />
                </SelectTrigger>
                <SelectContent>
                  {giftTier && giftOptions[giftTier].map(option => <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="senderName">Your Name *</Label>
              <Input id="senderName" placeholder="John Doe" value={giftForm.senderName} onChange={e => setGiftForm({
              ...giftForm,
              senderName: e.target.value
            })} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipientName">Recipient&apos;s Name *</Label>
              <Input id="recipientName" placeholder="Jane Smith" value={giftForm.recipientName} onChange={e => setGiftForm({
              ...giftForm,
              recipientName: e.target.value
            })} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="recipientEmail">Recipient&apos;s Email *</Label>
              <Input id="recipientEmail" type="email" placeholder="jane@example.com" value={giftForm.recipientEmail} onChange={e => setGiftForm({
              ...giftForm,
              recipientEmail: e.target.value
            })} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Personal Message (Optional)</Label>
              <Textarea id="message" placeholder="Happy travels! I hope this helps make your next adventure even more amazing..." value={giftForm.message} onChange={e => setGiftForm({
              ...giftForm,
              message: e.target.value
            })} rows={4} />
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setGiftModalOpen(false)} className="flex-1" disabled={giftLoading}>
              Cancel
            </Button>
            <Button onClick={handleGiftSubmit} className="flex-1 bg-gradient-to-r from-primary to-accent text-white" disabled={giftLoading}>
              {giftLoading ? <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent animate-spin rounded-full" />
                  Sending...
                </div> : <>
                  <Heart className="h-4 w-4 mr-2" />
                  Send Gift
                </>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checkout Confirmation Modal */}
      <CheckoutConfirmationModal
        open={checkoutModalOpen}
        onOpenChange={setCheckoutModalOpen}
        purchaseType={pendingPurchase?.type || 'subscription'}
        tier={pendingPurchase?.tier || ''}
        duration={pendingPurchase?.duration}
        price={pendingPurchase?.price || 0}
        onConfirm={handleConfirmCheckout}
        giftDetails={pendingPurchase?.giftDetails}
      />
    </div>;
}